import { useCallback, useEffect, useRef, useState } from "react";
import { BLANK_QUOTE } from "../lib/utils.js";
import { createQuotePersistence } from "../lib/quotePersistence.js";
import { useLiveQuote } from "./useLiveQuote.js";

/* The builder's quote, its lock, the Unsaved dialog and the recovery autosave, wired to
   one quotePersistence instance. DataProvider uses this against Firestore; the quotes
   test fixture uses it against the fake Firestore, so the browser tests run the real
   saving code.

   `live` carries the latest lists and callbacks, so the persistence (created once) never
   reads a stale closure. */
export function useQuoteWorkspace(live, { lockTimeoutMs } = {}) {
  const [q, setQ, getQ] = useLiveQuote(BLANK_QUOTE());
  const [highlightMissing, setHighlightMissing] = useState([]);
  const [busy, setBusy] = useState(null);
  const [unsavedPrompt, setUnsavedPrompt] = useState(null);
  const unsavedPromptOpen = useRef(false);
  const liveRef = useRef(live);
  liveRef.current = live;
  // The builder says here why a quote can't be saved right now (uploads running).
  const builderSaveBlocker = useRef(null);
  const setBuilderSaveBlocker = useCallback((fn) => { builderSaveBlocker.current = fn; }, []);

  const persistenceRef = useRef(null);
  if (!persistenceRef.current) {
    const L = liveRef;
    persistenceRef.current = createQuotePersistence({
      isAdmin: () => !!L.current.isAdmin,
      getQ,
      setQ,
      // A getter when the caller has one: the list it mirrors is newer than the last render
      // (the initial load installs the recovery copy before React renders again).
      getQuotes: () => (L.current.getQuotes ? L.current.getQuotes() : L.current.quotes),
      setQuotes: (update) => L.current.setQuotes(update),
      getJobs: () => L.current.jobs,
      getContacts: () => L.current.contacts,
      getClients: () => L.current.clients,
      storage: L.current.storage,
      getImages: (...a) => L.current.getImages(...a),
      saveImages: (...a) => L.current.saveImages(...a),
      deleteAttachmentFile: (...a) => L.current.deleteAttachmentFile?.(...a),
      notify: L.current.notify,
      commitContactMatches: (...a) => L.current.commitContactMatches(...a),
      syncJobRevenueFromQuote: (...a) => L.current.syncJobRevenueFromQuote(...a),
      getEquipmentMap: () => L.current.getEquipmentMap(),
      clearHighlights: () => setHighlightMissing([]),
      getSaveBlocker: () => builderSaveBlocker.current?.() || "",
      onBusyChange: setBusy,
      lockTimeoutMs,
      askUnsaved: (ctx) => new Promise((resolve) => {
        // One question at a time: a second one while the first is open stays where it is.
        if (unsavedPromptOpen.current) { resolve("cancel"); return; }
        unsavedPromptOpen.current = true;
        setUnsavedPrompt({
          ...ctx,
          done: (choice) => { unsavedPromptOpen.current = false; setUnsavedPrompt(null); resolve(choice); },
        });
      }),
    });
  }
  const persistence = persistenceRef.current;

  /* ═══ THE RECOVERY COPY (debounced autosave) ═══
     Written only while the open quote holds unsaved edits and nothing is saving. The lean
     projection (lib/draftRecord.js), never raw builder state: base64 previews and File
     objects pushed app/nycap-draft past Firestore's 1 MiB field cap and wedged the tab's
     write stream (2026-08-21). Previews go to the local image cache instead. */
  const saveTimer = useRef(null);
  // Runs again when the lock lifts (`busy`), so edits made just before a save that failed
  // or timed out still get their copy.
  useEffect(() => {
    if (!live.loaded) return;
    if (!persistence.shouldAutosave(q)) {
      // Edits undone by hand leave the quote clean: a copy written for them would bring
      // them back on a reload, so it is marked discarded now.
      persistence.retireIfClean(q);
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      // Asked again now: a save or a Discard may have come after this was scheduled.
      if (!persistence.shouldAutosave(getQ())) return;
      persistence.writeRecoveryCopy(getQ());
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [q, busy, live.loaded, live.isAdmin, persistence, getQ]);

  return { q, setQ, getQ, busy, unsavedPrompt, persistence, setBuilderSaveBlocker, highlightMissing, setHighlightMissing };
}
